import { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { TextStyle, Color, FontSize } from '@tiptap/extension-text-style';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { TableKit } from '@tiptap/extension-table';
import Youtube from '@tiptap/extension-youtube';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  List,
  ListOrdered,
  Loader2,
  Quote,
  Link as LinkIcon,
  Strikethrough,
  Table as TableIcon,
  Underline as UnderlineIcon,
  Undo2,
  Redo2,
  Youtube as YoutubeIcon,
} from 'lucide-react';
import { cn } from '../lib/cn';

// Click-to-apply font sizes (em-based so they scale with the surrounding copy).
// '' = clear back to the inherited size.
const FONT_SIZES: Array<{ label: string; value: string }> = [
  { label: 'Normal', value: '' },
  { label: 'Small', value: '0.85em' },
  { label: 'Large', value: '1.25em' },
  { label: 'Huge', value: '1.6em' },
];

/**
 * WYSIWYG editor for rich copy. Click-to-format — no Markdown syntax for the
 * author to learn. Emits clean HTML via `onChange`; the server sanitizes it on
 * write and the rendering SPA re-sanitizes on render.
 *
 * Two variants:
 * - `basic` (default) — inline formatting for product Details / testimonials.
 * - `full` — page-building toolbar (underline, alignment, inline images via
 *   `onUploadImage`, tables, YouTube embeds) for the replicated-page editor.
 *
 * Controlled by `value` (HTML). External resets (loading a record into the
 * form, switching records) are synced in while the editor is unfocused.
 */
export type RichTextEditorProps = {
  value: string | null | undefined;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  variant?: 'basic' | 'full';
  /**
   * Uploads a picked image and resolves to the URL to insert. Required for the
   * image toolbar button to appear (keeps this package API-agnostic — the host
   * app owns the upload endpoint).
   */
  onUploadImage?: (file: File) => Promise<string>;
};

function ToolbarButton({
  onClick,
  active,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40',
        active && 'bg-muted text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function Toolbar({
  editor,
  full,
  onUploadImage,
}: {
  editor: Editor;
  full: boolean;
  onUploadImage?: (file: File) => Promise<string>;
}) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', prev ?? 'https://');
    if (url === null) return; // cancelled
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const setYoutube = () => {
    const url = window.prompt('YouTube video URL', 'https://www.youtube.com/watch?v=');
    if (!url) return;
    editor.chain().focus().setYoutubeVideo({ src: url }).run();
  };

  const pickImage = async (file: File | null) => {
    if (!file || !onUploadImage) return;
    setUploading(true);
    try {
      const src = await onUploadImage(file);
      editor.chain().focus().setImage({ src, alt: file.name }).run();
    } catch {
      window.alert('Image upload failed — check the file type/size and try again.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const inTable = editor.isActive('table');

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-input bg-muted/30 p-1">
      <ToolbarButton label="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton label="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Italic className="h-4 w-4" />
      </ToolbarButton>
      {full && (
        <ToolbarButton label="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarButton>
      )}
      <ToolbarButton label="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="h-4 w-4" />
      </ToolbarButton>
      <div className="mx-1 h-5 w-px bg-border" />
      <ToolbarButton label="Heading" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        <Heading2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton label="Subheading" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        <Heading3 className="h-4 w-4" />
      </ToolbarButton>
      {full && (
        <>
          <div className="mx-1 h-5 w-px bg-border" />
          <ToolbarButton label="Align left" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
            <AlignLeft className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton label="Align center" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
            <AlignCenter className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton label="Align right" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
            <AlignRight className="h-4 w-4" />
          </ToolbarButton>
        </>
      )}
      <div className="mx-1 h-5 w-px bg-border" />
      <ToolbarButton label="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <List className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton label="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton label="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        <Quote className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton label="Link" active={editor.isActive('link')} onClick={setLink}>
        <LinkIcon className="h-4 w-4" />
      </ToolbarButton>
      {full && (
        <>
          {onUploadImage && (
            <>
              <ToolbarButton label="Insert image" disabled={uploading} onClick={() => fileRef.current?.click()}>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              </ToolbarButton>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => void pickImage(e.target.files?.[0] ?? null)}
              />
            </>
          )}
          <ToolbarButton label="Embed YouTube video" onClick={setYoutube}>
            <YoutubeIcon className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            label={inTable ? 'Delete table' : 'Insert table'}
            active={inTable}
            onClick={() =>
              inTable
                ? editor.chain().focus().deleteTable().run()
                : editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
            }
          >
            <TableIcon className="h-4 w-4" />
          </ToolbarButton>
          {inTable && (
            <>
              <ToolbarButton label="Add row below" onClick={() => editor.chain().focus().addRowAfter().run()}>
                <span className="text-[10px] font-bold">+R</span>
              </ToolbarButton>
              <ToolbarButton label="Add column right" onClick={() => editor.chain().focus().addColumnAfter().run()}>
                <span className="text-[10px] font-bold">+C</span>
              </ToolbarButton>
              <ToolbarButton label="Delete row" onClick={() => editor.chain().focus().deleteRow().run()}>
                <span className="text-[10px] font-bold">−R</span>
              </ToolbarButton>
              <ToolbarButton label="Delete column" onClick={() => editor.chain().focus().deleteColumn().run()}>
                <span className="text-[10px] font-bold">−C</span>
              </ToolbarButton>
            </>
          )}
        </>
      )}
      <div className="mx-1 h-5 w-px bg-border" />
      {/* Text color — native picker; the swatch shows the current color. */}
      <label
        title="Text color"
        className="relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <span
          className="h-4 w-4 rounded-sm border"
          style={{ backgroundColor: (editor.getAttributes('textStyle').color as string) || '#000000' }}
        />
        <input
          type="color"
          className="absolute h-0 w-0 opacity-0"
          value={(editor.getAttributes('textStyle').color as string) || '#000000'}
          onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
        />
      </label>
      <ToolbarButton label="Clear color" onClick={() => editor.chain().focus().unsetColor().run()}>
        <span className="text-xs font-semibold">A̶</span>
      </ToolbarButton>
      {/* Font size */}
      <select
        title="Font size"
        aria-label="Font size"
        className="h-8 rounded-md border border-input bg-background px-1 text-xs text-foreground"
        value={(editor.getAttributes('textStyle').fontSize as string) || ''}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') editor.chain().focus().unsetFontSize().run();
          else editor.chain().focus().setFontSize(v).run();
        }}
      >
        {FONT_SIZES.map((s) => (
          <option key={s.label} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
      <div className="mx-1 h-5 w-px bg-border" />
      <ToolbarButton label="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
        <Undo2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton label="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
        <Redo2 className="h-4 w-4" />
      </ToolbarButton>
    </div>
  );
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  className,
  disabled,
  variant = 'basic',
  onUploadImage,
}: RichTextEditorProps) {
  const full = variant === 'full';
  const editor = useEditor({
    editable: !disabled,
    extensions: [
      // StarterKit bundles Link in v3; disable it so our configured Link wins.
      StarterKit.configure({ link: false }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      // TextStyle is the carrier mark for inline color + font-size.
      TextStyle,
      Color,
      FontSize,
      ...(full
        ? [
            Image,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            TableKit.configure({ table: { resizable: false } }),
            Youtube.configure({ nocookie: true, width: 480, height: 270 }),
          ]
        : []),
    ],
    content: value ?? '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm max-w-none px-3 py-2.5 focus:outline-none',
          full ? 'min-h-[320px]' : 'min-h-[180px]',
          'dark:prose-invert',
        ),
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },
    },
  });

  // Keep editable in sync if the prop flips.
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  // Sync external value resets (e.g. loading a different product) while the user
  // isn't actively typing, so we don't clobber in-progress edits.
  useEffect(() => {
    if (!editor) return;
    const next = value ?? '';
    if (!editor.isFocused && next !== editor.getHTML()) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, value]);

  return (
    <div className={cn('overflow-hidden rounded-md border border-input bg-background', className)}>
      {editor && <Toolbar editor={editor} full={full} onUploadImage={onUploadImage} />}
      <EditorContent editor={editor} />
    </div>
  );
}
